import React from "react";

import Table from "@cloudscape-design/components/table";
import Pagination from "@cloudscape-design/components/pagination";
import Header from "@cloudscape-design/components/header";
import Input from "@cloudscape-design/components/input";
import Button from "@cloudscape-design/components/button";
import Grid from "@cloudscape-design/components/grid";
import Box from "@cloudscape-design/components/box";
import Link from "@cloudscape-design/components/link";
import Select from "@cloudscape-design/components/select";
import Preferences from "@cloudscape-design/components/collection-preferences";
import Alert from "@cloudscape-design/components/alert";
import SpaceBetween from "@cloudscape-design/components/space-between";
import DateRangePicker from "@cloudscape-design/components/date-range-picker";
import FormField from "@cloudscape-design/components/form-field";
import Modal from "@cloudscape-design/components/modal";
import ExpandableSection from "@cloudscape-design/components/expandable-section";
import Tabs from "@cloudscape-design/components/tabs";

import PlayersAntiFraud from "./PlayersAntiFraud";

/* Recoil */
import { useRecoilValue } from "recoil";
import { userStore } from "../stores/access";
import { configStore } from "../stores/config";
/* ------- */

const ANTIFRAUD_SHOW_COLUMN = "playersAntiFraud";
const ACTIVE_TAB_STORAGE_KEY = "players.sessions.activeTab";
const DEFAULT_PAGE_SIZE = Number(localStorage.getItem("players.sessions.perPage")) || 15;
const PAGE_SIZE_OPTIONS = [
	{ value: 15, label: "15" },
	{ value: 30, label: "30" },
	{ value: 50, label: "50" },
	{ value: 100, label: "100" },
	{ value: 200, label: "200" },
];
const DEFAULT_VISIBLE_COLUMNS = ["uid", "date", "ip", "country", "timezone", "browser", "os", "type", "model", "userAgent"];
const REQUIRED_VISIBLE_COLUMNS = ["uid"];
const PRIMARY_FILTER_FIELDS = ["ip"];
const SESSION_FILTER_FIELDS = [
	{ id: "sessionType", label: "Session type", placeholder: "Session type" },
	{ id: "method", label: "Method", placeholder: "Method" },
	{ id: "browser", label: "Browser", placeholder: "Browser" },
	{ id: "country", label: "Country", placeholder: "Country" },
	{ id: "timezone", label: "Timezone", placeholder: "Timezone" },
	{ id: "ip", label: "IP", placeholder: "IP" },
	{ id: "lang", label: "Lang", placeholder: "Lang" },
	{ id: "origin", label: "Origin", placeholder: "Origin" },
	{ id: "os", label: "OS", placeholder: "OS" },
	{ id: "region", label: "Region", placeholder: "Region" },
	{ id: "userAgent", label: "User-agent", placeholder: "User-agent" },
	{ id: "screen", label: "Screen", placeholder: "Screen" },
	{ id: "type", label: "Type", placeholder: "Type" },
	{ id: "model", label: "Model", placeholder: "Device model" },
	{ id: "brand", label: "Brand", placeholder: "Brand" },
];
const VISIBLE_CONTENT_OPTIONS = [
	// { id: "id", label: "ID" },
	{ id: "date", label: "Session date" },
	{ id: "ip", label: "IP" },
	{ id: "country", label: "Country" },
	{ id: "timezone", label: "Timezone" },
	{ id: "region", label: "Region" },
	{ id: "browser", label: "Browser" },
	{ id: "os", label: "OS" },
	{ id: "type", label: "Type" },
	{ id: "brand", label: "Brand" },
	{ id: "model", label: "Model" },
	{ id: "screen", label: "Screen" },
	{ id: "sessionType", label: "Session type" },
	{ id: "method", label: "Method" },
	{ id: "lang", label: "Lang" },
	{ id: "origin", label: "Origin" },
	{ id: "userAgent", label: "User-agent" },
];
const EXPORT_FORMAT_OPTIONS = [
	{ value: "json", label: "JSON" },
	{ value: "csv", label: "CSV" },
	{ value: "xlsx", label: "XLSX" },
];

function getExportOption(format) {
	return EXPORT_FORMAT_OPTIONS.find((option) => option.value === format) || EXPORT_FORMAT_OPTIONS[0];
}

function flattenExportValue(value) {
	if (value === null || typeof value === "undefined") return "";
	if (typeof value === "object") return JSON.stringify(value);
	return value;
}

function getExportColumns(data, visibleColumns = []) {
	if (Array.isArray(visibleColumns) && visibleColumns.length) return normalizeVisibleColumns(visibleColumns);

	const columns = [];
	data.forEach((item) => {
		Object.keys(item).forEach((key) => {
			if (!columns.includes(key)) columns.push(key);
		});
	});

	return columns;
}

function normalizeVisibleColumns(columns = DEFAULT_VISIBLE_COLUMNS) {
	const visibleColumns = Array.isArray(columns) ? columns : DEFAULT_VISIBLE_COLUMNS;
	const normalizedColumns = [...REQUIRED_VISIBLE_COLUMNS];

	visibleColumns.forEach((column) => {
		if (!normalizedColumns.includes(column)) normalizedColumns.push(column);
	});

	return normalizedColumns;
}

function getOptionalVisibleColumns(columns = DEFAULT_VISIBLE_COLUMNS) {
	return normalizeVisibleColumns(columns).filter((column) => !REQUIRED_VISIBLE_COLUMNS.includes(column));
}

function escapeCsvValue(value) {
	const stringValue = String(flattenExportValue(value));
	return /[",\n\r]/.test(stringValue) ? `"${stringValue.replace(/"/g, '""')}"` : stringValue;
}

function getCsvContent(data, visibleColumns) {
	const columns = getExportColumns(data, visibleColumns);
	const rows = data.map((item) => columns.map((column) => escapeCsvValue(item[column])).join(","));

	return [columns.join(","), ...rows].join("\n");
}

function escapeXml(value) {
	return String(flattenExportValue(value)).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&apos;");
}

function getColumnLetter(index) {
	let column = "";
	let current = index + 1;

	while (current > 0) {
		const remainder = (current - 1) % 26;
		column = String.fromCharCode(65 + remainder) + column;
		current = Math.floor((current - 1) / 26);
	}

	return column;
}

function createCrcTable() {
	const table = [];

	for (let i = 0; i < 256; i++) {
		let value = i;
		for (let j = 0; j < 8; j++) value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
		table[i] = value >>> 0;
	}

	return table;
}

const CRC_TABLE = createCrcTable();

function crc32(bytes) {
	let crc = 0xffffffff;

	for (let i = 0; i < bytes.length; i++) crc = CRC_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);

	return (crc ^ 0xffffffff) >>> 0;
}

function uint16(value) {
	return [value & 0xff, (value >>> 8) & 0xff];
}

function uint32(value) {
	return [value & 0xff, (value >>> 8) & 0xff, (value >>> 16) & 0xff, (value >>> 24) & 0xff];
}

function stringBytes(value) {
	return Array.from(new TextEncoder().encode(value));
}

function appendBytes(target, bytes) {
	for (let i = 0; i < bytes.length; i++) target.push(bytes[i]);
}

function createBlobFromZipParts(localParts, centralParts, endParts) {
	const output = new Uint8Array(localParts.length + centralParts.length + endParts.length);
	output.set(localParts, 0);
	output.set(centralParts, localParts.length);
	output.set(endParts, localParts.length + centralParts.length);

	return new Blob([output], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
}

function createZip(files) {
	const localParts = [];
	const centralParts = [];
	let offset = 0;

	files.forEach((file) => {
		const nameBytes = stringBytes(file.name);
		const dataBytes = stringBytes(file.content);
		const crc = crc32(dataBytes);
		const localHeader = [...uint32(0x04034b50), ...uint16(20), ...uint16(0), ...uint16(0), ...uint16(0), ...uint16(0), ...uint32(crc), ...uint32(dataBytes.length), ...uint32(dataBytes.length), ...uint16(nameBytes.length), ...uint16(0), ...nameBytes];

		appendBytes(localParts, localHeader);
		appendBytes(localParts, dataBytes);
		centralParts.push(...uint32(0x02014b50), ...uint16(20), ...uint16(20), ...uint16(0), ...uint16(0), ...uint16(0), ...uint16(0), ...uint32(crc), ...uint32(dataBytes.length), ...uint32(dataBytes.length), ...uint16(nameBytes.length), ...uint16(0), ...uint16(0), ...uint16(0), ...uint16(0), ...uint32(0), ...uint32(offset), ...nameBytes);
		offset += localHeader.length + dataBytes.length;
	});

	const end = [...uint32(0x06054b50), ...uint16(0), ...uint16(0), ...uint16(files.length), ...uint16(files.length), ...uint32(centralParts.length), ...uint32(localParts.length), ...uint16(0)];

	return createBlobFromZipParts(localParts, centralParts, end);
}

function getXlsxBlob(data, visibleColumns) {
	const columns = getExportColumns(data, visibleColumns);
	const rows = [columns, ...data.map((item) => columns.map((column) => flattenExportValue(item[column])))];
	const sheetData = rows
		.map((row, rowIndex) => {
			const cells = row
				.map((value, columnIndex) => {
					const cellRef = `${getColumnLetter(columnIndex)}${rowIndex + 1}`;
					return `<c r="${cellRef}" t="inlineStr"><is><t>${escapeXml(value)}</t></is></c>`;
				})
				.join("");

			return `<row r="${rowIndex + 1}">${cells}</row>`;
		})
		.join("");

	return createZip([
		{
			name: "[Content_Types].xml",
			content:
				'<?xml version="1.0" encoding="UTF-8"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/></Types>',
		},
		{ name: "_rels/.rels", content: '<?xml version="1.0" encoding="UTF-8"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>' },
		{ name: "xl/workbook.xml", content: '<?xml version="1.0" encoding="UTF-8"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="Export" sheetId="1" r:id="rId1"/></sheets></workbook>' },
		{ name: "xl/_rels/workbook.xml.rels", content: '<?xml version="1.0" encoding="UTF-8"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>' },
		{
			name: "xl/styles.xml",
			content:
				'<?xml version="1.0" encoding="UTF-8"?><styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><fonts count="1"><font><sz val="11"/><name val="Calibri"/></font></fonts><fills count="1"><fill><patternFill patternType="none"/></fill></fills><borders count="1"><border/></borders><cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs><cellXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/></cellXfs></styleSheet>',
		},
		{ name: "xl/worksheets/sheet1.xml", content: `<?xml version="1.0" encoding="UTF-8"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData>${sheetData}</sheetData></worksheet>` },
	]);
}

function getDefaultVisibleColumns() {
	try {
		const savedColumns = JSON.parse(localStorage.getItem("players.sessions.visibleColumns"));
		if (Array.isArray(savedColumns) && savedColumns.length) return normalizeVisibleColumns(savedColumns);
	} catch (e) {
		return normalizeVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
	}

	return normalizeVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
}

class PlayersSection extends React.PureComponent {
	constructor(props) {
		super(props);

		this.state = {
			loaded: true,
			data: [],
			error: {
				text: "",
				code: 0,
				timestamp: 0,
			},
			pagination: {
				current: 1,
				total: 0,
				count: 0,
			},
			perPage: DEFAULT_PAGE_SIZE,
			filters: {
				uid: "",
				sessionType: "",
				method: "",
				browser: "",
				country: "",
				timezone: "",
				ip: "",
				lang: "",
				origin: "",
				os: "",
				region: "",
				userAgent: "",
				screen: "",
				type: "",
				model: "",
				brand: "",
			},
			dateRange: this.getEmptyDateRange(),
			exportModalVisible: false,
			exportFormat: "json",
			sorting: {
				sortingColumn: {
					sortingField: "date",
				},
				isDescending: true,
			},
			activeTabId: sessionStorage.getItem(ACTIVE_TAB_STORAGE_KEY) || "sessions",
			visibleColumns: getDefaultVisibleColumns(),
		};
	}

	getEmptyDateRange() {
		return {
			type: "absolute",
			startDate: "",
			endDate: "",
		};
	}

	getDateRangeValue(value) {
		if (!value || (!value.startDate && !value.endDate)) return this.getEmptyDateRange();

		return {
			type: "absolute",
			startDate: value.startDate || "",
			endDate: value.endDate || "",
		};
	}

	isDateRangeValid(range) {
		if (!range || (!range.startDate && !range.endDate)) return { valid: true };

		if (range.type !== "absolute") return { valid: false, errorMessage: "Only absolute date range is supported" };
		if (!range.startDate || !range.endDate) return { valid: true };
		if (range.startDate > range.endDate) return { valid: false, errorMessage: "Start date should be earlier than end date" };

		return { valid: true };
	}

	componentDidMount() {
		this.loadSessions();
	}

	getOrderDirection() {
		return this.state.sorting.isDescending ? "DESC" : "ASC";
	}

	getOrderBy() {
		return this.state.sorting.sortingColumn.sortingField || "date";
	}

	getVisibleColumns() {
		return normalizeVisibleColumns(this.state.visibleColumns);
	}

	hasVisibleColumn(column) {
		return this.getVisibleColumns().includes(column);
	}

	getRequestFilters() {
		const filters = {};

		Object.keys(this.state.filters).forEach((key) => {
			const value = this.state.filters[key];
			if (key !== "uid" && !this.hasVisibleColumn(key)) return;
			if (typeof value === "string" && value.trim().length) filters[key] = value.trim();
		});

		if (this.hasVisibleColumn("date") && this.state.dateRange.startDate && this.state.dateRange.endDate) {
			filters.dateFrom = this.state.dateRange.startDate.split("+")[0];
			filters.dateTo = this.state.dateRange.endDate.split("+")[0];
		}

		return filters;
	}

	loadSessions(page = this.state.pagination.current) {
		this.setState({ loaded: false, data: [] });

		this.props.Interface.request(
			"get",
			{ class: "users", module: "usersSession", method: "list" },
			{
				page,
				limit: this.state.perPage,
				orderBy: this.getOrderBy(),
				orderDirection: this.getOrderDirection(),
				filters: this.getRequestFilters(),
			},
		)
			.then((response) => {
				const pagination = response.pagination || {};

				this.setState({
					loaded: true,
					data: response.items || [],
					error: {
						text: "",
						code: 0,
						timestamp: 0,
					},
					pagination: {
						current: pagination.page || page,
						total: pagination.pages || 0,
						count: pagination.total || 0,
					},
				});
			})
			.catch((error) => {
				if (typeof error.text === "undefined") error = { text: error.toString() };
				this.setState({
					loaded: true,
					error,
				});
			});
	}

	updateFilter(key, value) {
		this.setState({
			filters: {
				...this.state.filters,
				[key]: value,
			},
			pagination: {
				...this.state.pagination,
				current: 1,
			},
		});
	}

	updateDateRange(value) {
		this.setState({
			dateRange: this.getDateRangeValue(value),
			pagination: {
				...this.state.pagination,
				current: 1,
			},
		});
	}

	handleKeyPress = (event) => {
		if (event.detail.key === "Enter") {
			setTimeout(() => {
				this.loadSessions(1);
			}, 0);
		}
	};

	getDateRangePickerI18nStrings() {
		return {
			todayAriaLabel: "Today",
			nextMonthAriaLabel: "Next month",
			previousMonthAriaLabel: "Previous month",
			customRelativeRangeDurationLabel: "Duration",
			customRelativeRangeDurationPlaceholder: "Enter duration",
			customRelativeRangeOptionLabel: "Custom range",
			customRelativeRangeOptionDescription: "Define a custom range",
			customRelativeRangeUnitLabel: "Unit of time",
			formatRelativeRange: ({ startDate, endDate }) => {
				const start = new Date(startDate).toLocaleString("en-GB");
				const end = new Date(endDate).toLocaleString("en-GB");
				return `${start} - ${end}`;
			},
			formatUnit: (unit, amount) => `${unit}${amount > 1 ? "s" : ""}`,
			dateTimeConstraintText: "Date and time must be within range.",
			renderSelectedAbsoluteRangeAriaLive: (startDate, endDate) => `Selected from ${startDate} to ${endDate}`,
			labelRelativeRange: "Relative range",
			labelAbsoluteRange: "Absolute range",
			dateTimePickerAriaLabel: "Date and time picker",
			dateTimePickerLabel: "Date and Time",
			relativeModeTitle: "Relative mode",
			absoluteModeTitle: "Absolute mode",
			cancelButtonLabel: "Cancel",
			applyButtonLabel: "Apply",
			clearButtonLabel: "Clear date range",
		};
	}

	formatDate(item) {
		const sessionDate = Number(item.date);
		const date = Number.isFinite(sessionDate) && sessionDate > 0 ? new Date(sessionDate) : new Date(item.createdAt);

		if (Number.isNaN(date.getTime())) return "none";

		return date.toLocaleDateString() + " " + date.toLocaleTimeString();
	}

	getPreparedExportData(data) {
		const columns = getExportColumns(data, this.getVisibleColumns());
		return data.map((item) =>
			columns.reduce((accumulator, column) => {
				accumulator[column] = item[column];
				return accumulator;
			}, {}),
		);
	}

	saveData(data, format = "json") {
		const element = document.createElement("a");
		const fileName = this.props.config.app.name.toLowerCase() + "_players_sessions_" + +new Date() + "." + format;
		const exportData = this.getPreparedExportData(data);
		const visibleColumns = this.getVisibleColumns();
		let file;

		if (format === "csv") {
			file = new Blob([getCsvContent(exportData, visibleColumns)], { type: "text/csv;charset=utf-8;" });
		} else if (format === "xlsx") {
			file = getXlsxBlob(exportData, visibleColumns);
		} else {
			file = new Blob([JSON.stringify(exportData)], { type: "application/json;charset=utf-8;" });
		}

		element.href = URL.createObjectURL(file);
		element.download = fileName;
		document.body.appendChild(element);
		element.click();
		document.body.removeChild(element);
		URL.revokeObjectURL(element.href);
	}

	renderSessionsTable() {
		const visibleColumns = this.getVisibleColumns();
		const optionalVisibleColumns = getOptionalVisibleColumns(visibleColumns);
		const selectedSessionFilterFields = SESSION_FILTER_FIELDS.filter((field) => visibleColumns.includes(field.id));
		const primaryFilterFields = selectedSessionFilterFields.filter((field) => PRIMARY_FILTER_FIELDS.includes(field.id));
		const advancedFilterFields = selectedSessionFilterFields.filter((field) => !PRIMARY_FILTER_FIELDS.includes(field.id));
		const getTextFilterItem = (field) => ({
			id: field.id,
			colspan: 3,
			content: (
				<FormField key={field.id} label={field.label}>
					<Input disabled={!this.state.loaded} onKeyDown={this.handleKeyPress} placeholder={field.placeholder} value={this.state.filters[field.id]} onChange={(input) => this.updateFilter(field.id, input.detail.value)} />
				</FormField>
			),
		});
		const primaryFilterItems = [
			{
				id: "uid",
				colspan: 3,
				content: (
					<FormField key="uid" label="UID / UID list">
						<Input disabled={!this.state.loaded} onKeyDown={this.handleKeyPress} disableBrowserAutocorrect={true} placeholder="UID, comma or space separated" inputMode="search" type="search" value={this.state.filters.uid} onChange={(input) => this.updateFilter("uid", input.detail.value)} />
					</FormField>
				),
			},
			...primaryFilterFields.map(getTextFilterItem),
			...(visibleColumns.includes("date")
				? [
						{
							id: "date",
							colspan: 6,
							content: (
								<FormField key="date" label="Session date range">
									<DateRangePicker disabled={!this.state.loaded} value={this.state.dateRange} placeholder="All time" relativeOptions={[]} isValidRange={(range) => this.isDateRangeValid(range)} onKeyDown={this.handleKeyPress} onChange={({ detail }) => this.updateDateRange(detail.value)} i18nStrings={this.getDateRangePickerI18nStrings()} />
								</FormField>
							),
						},
					]
				: []),
		];
		const advancedFilterItems = advancedFilterFields.map(getTextFilterItem);

		return (
			<>
				<Modal
					visible={this.state.exportModalVisible}
					onDismiss={() => this.setState({ exportModalVisible: false })}
					header="Export players sessions"
					footer={
						<Box float="right">
							<SpaceBetween direction="horizontal" size="xs">
								<Button variant="link" onClick={() => this.setState({ exportModalVisible: false })}>
									Cancel
								</Button>
								<Button
									variant="primary"
									onClick={() => {
										this.saveData(this.state.data, this.state.exportFormat);
										this.setState({ exportModalVisible: false });
									}}
									disabled={!this.state.loaded || !this.state.data.length}>
									Export
								</Button>
							</SpaceBetween>
						</Box>
					}>
					<FormField label="Export format">
						<Select selectedOption={getExportOption(this.state.exportFormat)} options={EXPORT_FORMAT_OPTIONS} onChange={(option) => this.setState({ exportFormat: option.detail.selectedOption.value })} />
					</FormField>
				</Modal>
				{this.state.loaded && this.state.error.text.length ? (
					<Box
						margin={{ bottom: "m" }}
						children={
							<Alert
								type="error"
								header="Error loading data:"
								children={
									<>
										<Box variant="p" children={this.state.error.text} />
										<hr />
										<Box
											variant="p"
											children={
												<>
													request error code: {this.state.error.code} | request timestamp: {this.state.error.timestamp}
												</>
											}
										/>
									</>
								}
							/>
						}
					/>
				) : (
					<></>
				)}

				<Table
					stickyHeader={true}
					wrapLines={true}
					trackBy="id"
					visibleColumns={visibleColumns}
					loading={!this.state.loaded}
					sortingColumn={{ sortingField: this.getOrderBy() }}
					sortingDescending={this.state.sorting.isDescending}
					onSortingChange={(sorting) => {
						this.setState({ sorting: sorting.detail, pagination: { ...this.state.pagination, current: 1 } }, () => {
							this.loadSessions(1);
						});
					}}
					columnDefinitions={[
						{ id: "id", header: "ID", sortingField: "id", cell: (item) => item.id },
						{
							id: "uid",
							header: "UID",
							sortingField: "uid",
							cell: (item) =>
								item.uid ? (
									<Link
										variant="normal"
										onFollow={() => {
											this.props.Interface.loadInfo(item.uid);
										}}
										children={item.uid}
									/>
								) : (
									"none"
								),
						},
						{ id: "date", header: <span style={{ whiteSpace: "nowrap" }}>Session date</span>, sortingField: "date", cell: (item) => this.formatDate(item) },
						{ id: "ip", header: <span style={{ whiteSpace: "nowrap" }}>IP</span>, sortingField: "ip", cell: (item) => item.ip || "none" },
						{ id: "country", header: <span style={{ whiteSpace: "nowrap" }}>Country</span>, sortingField: "country", cell: (item) => item.country || "none" },
						{ id: "timezone", header: <span style={{ whiteSpace: "nowrap" }}>Timezone</span>, sortingField: "timezone", cell: (item) => item.timezone || "—" },
						{ id: "region", header: <span style={{ whiteSpace: "nowrap" }}>Region</span>, sortingField: "region", cell: (item) => item.region || "none" },
						{ id: "browser", header: <span style={{ whiteSpace: "nowrap" }}>Browser</span>, sortingField: "browser", cell: (item) => item.browser || "none" },
						{ id: "os", header: <span style={{ whiteSpace: "nowrap" }}>OS</span>, sortingField: "os", cell: (item) => item.os || "none" },
						{ id: "type", header: <span style={{ whiteSpace: "nowrap" }}>Type</span>, sortingField: "type", cell: (item) => item.type || "none" },
						{ id: "brand", header: <span style={{ whiteSpace: "nowrap" }}>Brand</span>, sortingField: "brand", cell: (item) => item.brand || "none" },
						{ id: "model", header: <span style={{ whiteSpace: "nowrap" }}>Model</span>, sortingField: "model", cell: (item) => <span>{item.model || "none"}</span> },
						{ id: "screen", header: <span style={{ whiteSpace: "nowrap" }}>Screen</span>, sortingField: "screen", cell: (item) => item.screen || "none" },
						{ id: "sessionType", header: <span style={{ whiteSpace: "nowrap" }}>Session type</span>, sortingField: "sessionType", cell: (item) => item.sessionType || "none" },
						{ id: "method", header: <span style={{ whiteSpace: "nowrap" }}>Method</span>, sortingField: "method", cell: (item) => item.method || "none" },
						{ id: "lang", header: <span style={{ whiteSpace: "nowrap" }}>Lang</span>, sortingField: "lang", cell: (item) => item.lang || "none" },
						{ id: "origin", header: <span style={{ whiteSpace: "nowrap" }}>Origin</span>, sortingField: "origin", cell: (item) => item.origin || "none" },
						{ id: "userAgent", header: <span style={{ whiteSpace: "nowrap" }}>User-agent</span>, sortingField: "userAgent", cell: (item) => <span style={{ whiteSpace: "nowrap" }}>{item.userAgent || "none"}</span> },
					]}
					items={this.state.data}
					pagination={
						<Pagination
							disabled={!this.state.loaded}
							currentPageIndex={this.state.pagination.current}
							pagesCount={this.state.pagination.total}
							onChange={(page) => {
								const current = page.detail.currentPageIndex;
								this.setState({ pagination: { ...this.state.pagination, current } }, () => {
									this.loadSessions(current);
								});
							}}
						/>
					}
					preferences={
						<Preferences
							cancelLabel="Cancel"
							confirmLabel="Confirm"
							pageSizePreference={{ title: "Sessions per page", options: PAGE_SIZE_OPTIONS }}
							visibleContentPreference={{
								title: "Select visible columns",
								options: [
									{
										label: "Columns",
										options: VISIBLE_CONTENT_OPTIONS,
									},
								],
							}}
							preferences={{
								pageSize: this.state.perPage,
								visibleContent: optionalVisibleColumns,
							}}
							title="Preferences"
							onConfirm={(preferences) => {
								const nextVisibleColumns = normalizeVisibleColumns(preferences.detail.visibleContent || DEFAULT_VISIBLE_COLUMNS);

								localStorage.setItem("players.sessions.perPage", preferences.detail.pageSize);
								localStorage.setItem("players.sessions.visibleColumns", JSON.stringify(nextVisibleColumns));
								this.setState({ perPage: preferences.detail.pageSize, visibleColumns: nextVisibleColumns, pagination: { ...this.state.pagination, current: 1 } }, () => {
									this.loadSessions(1);
								});
							}}
						/>
					}
					filter={
						<Box
							margin={{ bottom: "m" }}
							children={
								<SpaceBetween size="s">
									<Grid gridDefinition={primaryFilterItems.map((item) => ({ colspan: item.colspan }))} children={primaryFilterItems.map((item) => item.content)} />
									{advancedFilterItems.length ? (
										<ExpandableSection headerText={`Advanced filters (${advancedFilterItems.length})`}>
											<Grid gridDefinition={advancedFilterItems.map((item) => ({ colspan: item.colspan }))} children={advancedFilterItems.map((item) => item.content)} />
										</ExpandableSection>
									) : (
										<></>
									)}
								</SpaceBetween>
							}
						/>
					}
					header={
						<Box
							margin={{ bottom: "m" }}
							children={
								<Header
									children="Players Sessions"
									counter={<>({this.state.pagination.count})</>}
									actions={
										<SpaceBetween size="l" direction="horizontal">
											<Button
												disabled={!this.state.loaded || !this.state.data.length}
												className={this.props.styles["players-search-button"]}
												onClick={() => {
													this.setState({ exportModalVisible: true });
												}}
												variant="primary"
												children="Export"
											/>
											<Button
												disabled={!this.state.loaded}
												className={this.props.styles["players-search-button"]}
												onClick={() => {
													this.loadSessions(1);
												}}
												variant="primary"
												children="Find"
											/>
										</SpaceBetween>
									}
									description="Below listed matched players sessions"
								/>
							}
						/>
					}
					empty="No players sessions found with requested filter"
				/>
			</>
		);
	}

	render() {
		const showColumn = Array.isArray(this.props.user?.showColumn) ? this.props.user.showColumn : [];
		const tabs = [
			{
				id: "sessions",
				label: "Sessions",
				content: this.renderSessionsTable(),
			},
		];

		if (Number(this.props.user?.level) >= 6 || showColumn.includes(ANTIFRAUD_SHOW_COLUMN)) {
			tabs.push({
				id: "antifraud",
				label: "AntiFraud",
				content: <PlayersAntiFraud Interface={this.props.Interface} />,
			});
		}

		const activeTabId = tabs.some((tab) => tab.id === this.state.activeTabId) ? this.state.activeTabId : "sessions";

		return (
			<Tabs
				tabs={tabs}
				activeTabId={activeTabId}
				onChange={({ detail }) => {
					sessionStorage.setItem(ACTIVE_TAB_STORAGE_KEY, detail.activeTabId);
					this.setState({ activeTabId: detail.activeTabId });
				}}
			/>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const config = useRecoilValue(configStore);
		const user = useRecoilValue(userStore);

		return <Component {...props} config={config} user={user} />;
	};
}

export default withRecoil(PlayersSection);
